import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Nav, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import API_CONFIG  from '../../config/api';
import ChangePasswordModal from './ChangePasswordModal';
import { 
  BsEnvelope, 
  BsFileText, 
  BsDownload, 
  BsArrowClockwise, 
  BsBoxArrowRight,
  BsKey,
  BsHouse,
  BsPerson,
  BsCalendar,
  BsClock,
  BsStar,
  BsGenderAmbiguous
} from 'react-icons/bs';

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('newsletter');
  const [newsletterData, setNewsletterData] = useState([]);
  const [interestData, setInterestData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const API_BASE = API_CONFIG.BASE_URL;

  useEffect(() => {
    fetchData(false); // Don't show toast on initial load
  }, []);

  const fetchData = async (showToast = true) => {
    setLoading(true);
    try {
      const [newsletterRes, interestRes] = await Promise.all([
        fetch(`${API_BASE}/get-data.php?type=newsletter`),
        fetch(`${API_BASE}/get-data.php?type=interest`)
      ]);

      const newsletter = await newsletterRes.json();
      const interest = await interestRes.json();

      setNewsletterData(newsletter.data || []);
      setInterestData(interest.data || []);
      
      // Only show toast if manually refreshed
      if (showToast) {
        toast.success('✅ Data refreshed');
      }
    } catch (error) {
      toast.error('❌ Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (type) => {
    window.open(`${API_BASE}/download.php?type=${type}`, '_blank');
    toast.success('📥 Download started');
  };

  const handleLogout = () => {
    onLogout();
    navigate('/admin/login');
  };

  return (
    <div style={{ background: 'var(--beige-bg)', minHeight: '100vh' }}>
      {/* Premium Header */}
      <div className="admin-header">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} md={12} className="mb-3 mb-lg-0">
              <div className="d-flex align-items-center gap-3">
                <div className="admin-logo-circle">
                  <img 
                    src="/images/logo.png" 
                    alt="Nisa Care" 
                    style={{ height: '40px', width: 'auto' }}
                  />
                </div>
                <div>
                  <h1 className="admin-title">Admin Dashboard</h1>
                  <p className="admin-subtitle">Manage your community data</p>
                </div>
              </div>
            </Col>
            <Col lg={6} md={12}>
              <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                <Button 
                  variant="outline-secondary"
                  className="admin-header-btn"
                  onClick={() => setShowPasswordModal(true)}
                >
                  <BsKey className="me-2" />
                  Password
                </Button>
                <Button 
                  variant="outline-secondary"
                  className="admin-header-btn"
                  onClick={() => navigate('/')}
                >
                  <BsHouse className="me-2" />
                  Home
                </Button>
                <Button 
                  variant="danger"
                  className="admin-header-btn"
                  onClick={handleLogout}
                >
                  <BsBoxArrowRight className="me-2" />
                  Logout
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="py-5">
        {/* Premium Stats Cards */}
        <Row className="g-4 mb-5">
          <Col lg={6}>
            <Card className="premium-stat-card">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="stat-icon-wrapper newsletter-icon">
                    <BsEnvelope size={28} />
                  </div>
                  <Button 
                    className="download-btn"
                    onClick={() => handleDownload('newsletter')}
                    size="sm"
                  >
                    <BsDownload className="me-1" />
                    Export
                  </Button>
                </div>
                <h3 className="stat-label">Newsletter Subscribers</h3>
                <div className="stat-number">{newsletterData.length}</div>
                <p className="stat-footer">Total subscriptions received</p>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="premium-stat-card">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="stat-icon-wrapper interest-icon">
                    <BsFileText size={28} />
                  </div>
                  <Button 
                    className="download-btn"
                    onClick={() => handleDownload('interest')}
                    size="sm"
                  >
                    <BsDownload className="me-1" />
                    Export
                  </Button>
                </div>
                <h3 className="stat-label">Interest Forms</h3>
                <div className="stat-number">{interestData.length}</div>
                <p className="stat-footer">Community members showing interest</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Premium Tabs */}
        <div className="premium-tabs-wrapper mb-4">
          <Nav className="premium-tabs">
            <Nav.Item>
              <Nav.Link 
                className={activeTab === 'newsletter' ? 'active' : ''}
                onClick={() => setActiveTab('newsletter')}
              >
                <BsEnvelope className="me-2" />
                Newsletter
                <Badge bg="light" text="dark" className="ms-2">
                  {newsletterData.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                className={activeTab === 'interest' ? 'active' : ''}
                onClick={() => setActiveTab('interest')}
              >
                <BsFileText className="me-2" />
                Interest Forms
                <Badge bg="light" text="dark" className="ms-2">
                  {interestData.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Button 
            variant="outline-primary" 
            size="sm"
            className="refresh-btn"
            onClick={() => fetchData(true)}
            disabled={loading}
          >
            <BsArrowClockwise className={loading ? 'spinning' : ''} />
          </Button>
        </div>

        {/* Premium Data Table */}
        <Card className="premium-data-card">
          <Card.Body className="p-0">
            {loading ? (
              <div className="loading-state">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Loading data...</p>
              </div>
            ) : (
              <>
                {activeTab === 'newsletter' && (
                  <>
                    {newsletterData.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <BsEnvelope size={80} />
                        </div>
                        <h5>No Subscribers Yet</h5>
                        <p>Newsletter subscribers will appear here once they sign up</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="premium-table mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: '80px' }}>#</th>
                              <th>
                                <BsEnvelope className="me-2" size={16} />
                                Email Address
                              </th>
                              <th style={{ width: '180px' }}>
                                <BsCalendar className="me-2" size={16} />
                                Date
                              </th>
                              <th style={{ width: '150px' }}>
                                <BsClock className="me-2" size={16} />
                                Time
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {newsletterData.map((item, index) => (
                              <tr key={index} className="table-row-hover">
                                <td className="row-number">{index + 1}</td>
                                <td className="email-cell">{item.Email}</td>
                                <td>{item.Date}</td>
                                <td>{item.Time}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'interest' && (
                  <>
                    {interestData.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon">
                          <BsFileText size={80} />
                        </div>
                        <h5>No Submissions Yet</h5>
                        <p>Interest form submissions will appear here once users express interest</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table className="premium-table mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: '60px' }}>#</th>
                              <th>
                                <BsPerson className="me-2" size={16} />
                                Name
                              </th>
                              <th>
                                <BsEnvelope className="me-2" size={16} />
                                Email
                              </th>
                              <th style={{ width: '110px' }}>
                                <BsGenderAmbiguous className="me-2" size={16} />
                                Gender
                              </th>
                              <th style={{ width: '110px' }}>Age</th>
                              <th style={{ width: '110px' }}>
                                <BsStar className="me-2" size={16} />
                                Interest
                              </th>
                              <th>Topics</th>
                              <th style={{ width: '130px' }}>
                                <BsCalendar className="me-2" size={16} />
                                Date
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {interestData.map((item, index) => (
                              <tr key={index} className="table-row-hover">
                                <td className="row-number">{index + 1}</td>
                                <td className="name-cell">{item['Full Name']}</td>
                                <td className="email-cell">{item.Email}</td>
                                <td>{item.Gender}</td>
                                <td>
                                  <Badge bg="secondary" className="age-badge">
                                    {item['Age Range']}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge bg="warning" text="dark" className="interest-badge">
                                    {'⭐'.repeat(item['Interest Level'])}
                                  </Badge>
                                </td>
                                <td className="topics-cell">{item.Topics}</td>
                                <td>{item.Date}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Change Password Modal */}
      <ChangePasswordModal 
        show={showPasswordModal} 
        handleClose={() => setShowPasswordModal(false)} 
      />
    </div>
  );
};

export default AdminDashboard;